import React, { useContext } from "react"
import { Link as LinkRouter } from "../Router"
import { PagesContext, BlogArchiveSlugContext } from "../../context"
import GalleryLink from "./GalleryLink"
import { forceTrailingSlash } from "../../util/urlTransforms"
import { convertToInternalUrl } from "../../util/urlTransforms"
import { getCurrentLocale } from '../../api/index'
import { getLocaleInfo } from '../../util/contentModelToData'
import { BLOG_POST } from '../../api/contentTypes'

// if the link is an itemGalleryLink return a GalleryLink otherwise return ResolveLink defined below
const Link = ({ link, onClick, noScroll=false, className, children, translations }) => {

  if( link.contentType == "itemGalleryLink" ) {
    return <GalleryLink link={ link } className={ className } children={ children } translations={ translations }/>
  }

  return <ResolveLink link={ link } onClick={ onClick } noScroll={ noScroll } className={ className } children={ children }/>

}


const ResolveLink = ({ link: { title, url, forceExternal, internalLink = {}, anchor, autoAnchor, contentType} = {},
                onClick,
                noScroll = false,
                className,
                children,
            }) => {
    
    const { slug: currentPageSlug, subPages, subPagesSpecial } = useContext(PagesContext)
    
    // If blogSlug is defined we use that. We must pass the blogSlug into any showModal that might render a link, since showModal creates new context useSiteData is not available
    // If blogSlug is undefined use useSiteData
    const blogArchiveSlug = '/' + useContext(BlogArchiveSlugContext).blogSlug + '/'

    const slug = internalLink?.contentType === BLOG_POST ? blogArchiveSlug + internalLink?.slug : internalLink?.slug

    const currentLocale = getCurrentLocale()
    const domain = getLocaleInfo(currentLocale).link
    
    // Item: Link
    // Sometimes it is not possible to use an internal link to get to the content we want. For instance when linking to a subpage from outside the parent.
    // /surfcourses/ is the parent page and have level-1, level-2 & level-3 as their subpages
    // a content carousel has been setup with promo items that uses an internal link directly to the subpages e.g level-1
    // this content carousel only works when it is placed on /surfcourses/ as the internal link only contains levlel-x but will get the prefix based on the parent page
    // when the same content carousel is places on /surfcamps/ the internal links to level-x are not resolved since they are not rendered within their parent
    // for these cases we can set an external link to the content and convert it to an internal link
    // turns an external link into an internal link
    // e.g: https://lapointcamps.com/surfcourses/level-1/ returns /surfcourses/level-1/
    // while https://shop.lapointcamps.com returns https://shop.lapointcamps.com
    // and https://lapointcamps.freshdesk.com/support/home returns https://lapointcamps.freshdesk.com/support/home
    // 
    // If forceExternal is set to yes then we should not check if an external url is internal... 
    // I.e we should not convert https://www.lapoint.no/surfeskole/hoddevik/ to /surfeskole/hoddevik/ 
    // Unless we are on lapoint.no as we don't want to open a link to the same domain in a new window
    if( forceExternal && url ) {
        // if the URL that we want to force external is on the same domain as the current site we should not force external instead make it a relative link
        
        // first get the current domain without protocol        
        const targetDomain = domain.replace(/^https:\/\/www\./, '').replace(/\/+$/, '')

        // then check if the external url contains the targetDomain
        if( url.includes( targetDomain ) ) {
            forceExternal = false
        }
    }
        
    const internalURL = forceExternal ? false : url ? convertToInternalUrl( url ) : url

    if (url && !internalURL) {
        
        // special case tiny hack for the sitemap.xml
        let sitemap = "/sitemap.xml"
        if( url.substring(url.length - sitemap.length) === sitemap ) {
            return (
                <a                
                    onClick={ onClick }
                    className={ "a-link" + (className ? " " + className : "") }
                    href={ url }
                >
                    { children ? null : title }
                    { children }
                </a>
            );
        }

        return (
            <a
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={ onClick }
                className={ "a-link" + (className ? " " + className : "") }
                href={ url }
            >
                { children ? null : title }
                { children }
            </a>
        );
    }

    if(autoAnchor && !anchor) {
        anchor = { anchorName: slug }
    }

    const thisIsASubpage = subPages.filter(page => {
        return page.slug === slug
    }).length > 0

    const thisIsASubpageSpecial = subPagesSpecial.filter(page => {
        return page.slug === slug
    }).length > 0

    const prefix = thisIsASubpage || thisIsASubpageSpecial ? '/' + currentPageSlug + "/" : (slug && slug[0] === '/' ? '' : '/')
    const to = internalURL ? internalURL : forceTrailingSlash( ((slug ? (prefix + slug) : '') + (anchor ? "/#" + anchor.anchorName : "")) || "/" )

    if( !internalURL && !slug ) {
        //console.log( 'I dont know where to go:', title )
    }

    return (
        <LinkRouter
            className={ "a-link" + (className ? " " + className : "") }
            to={ to }
            onClick={ (e) => {
                if (typeof window !== "undefined" && !anchor && !noScroll && !e.metaKey && !e.ctrlKey) {
                    window.scrollTo(0, 0);
                }
                onClick && onClick();
            } }
        >
            { children ? null : title }
            { children }
        </LinkRouter>
    )
}

export default Link